import React, { useEffect, useState, useCallback, useMemo } from "react"
import Chart from "react-apexcharts"
import { Text, Button } from "@/components/atoms"
import { Input } from "@/components/molecules"
import { dateMask } from "@/utils/masks"
import { getDashboard } from "@/services/dashboardService"
import { useAuth } from "@/contexts/AuthContext"

interface MentorData {
  mentorId: number
  mentorNome: string
  qtdEmpresasPrincipal: number
  qtdEmpresasEspecialista: number
  sessoesRealizadas: number
}

interface DashboardApiResponse {
  qtdMentorEspecialista: string
  qtdMentorPrincipal: string
  qtdEmpresasGiants: string
  totalRealizadas: string
  sessoesOnline: string
  sessoesInCompany: string
  realizadasComoPrincipal: string
  realizadasComoEspecialista: string
  totalMentoresEspecialistas?: string
  qtdMentoresEspecialistasUnicos?: string
  qtdMentoresPrincipaisUnicos?: string
  totalMentoresPrincipais?: string
  listaMentores?: MentorData[]
}

interface DashboardCardsProps {
  dateRange: { from: string; to: string }
  onDateChange: (type: "from" | "to", value: string) => void
}

export const DashboardCards = ({ dateRange, onDateChange }: DashboardCardsProps) => {
  const { user } = useAuth()
  const [dashboardData, setDashboardData] = useState<DashboardApiResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState("")

  const isAdmin = user?.admin === true
  const num = (value: string | number | undefined) => Number(value) || 0

  // --- Lógica de Dados ---

  const filteredMentores = useMemo(() => {
    if (!dashboardData?.listaMentores) return []
    return dashboardData.listaMentores.filter((mentor) =>
      mentor.mentorNome.toLowerCase().includes(searchTerm.toLowerCase())
    )
  }, [searchTerm, dashboardData?.listaMentores])

  // REESCRITO: Ordenação do Top 10 pela soma total de empresas atendidas
  const comparisonChartData = useMemo(() => {
    if (!dashboardData?.listaMentores) return { series: [], categories: [] }

    const topMentores = [...dashboardData.listaMentores]
      .sort((a, b) => {
        const totalA = a.qtdEmpresasPrincipal + a.qtdEmpresasEspecialista
        const totalB = b.qtdEmpresasPrincipal + b.qtdEmpresasEspecialista
        return totalB - totalA // Do maior para o menor
      })
      .slice(0, 10)

    return {
      categories: topMentores.map((m) => m.mentorNome),
      series: [
        { name: "Emp. Principal", data: topMentores.map((m) => m.qtdEmpresasPrincipal) },
        { name: "Emp. Especialista", data: topMentores.map((m) => m.qtdEmpresasEspecialista) },
      ],
    }
  }, [dashboardData?.listaMentores])

  const formatToISO = (dateStr: string) => {
    if (dateStr.length !== 10) return null
    const [day, month, year] = dateStr.split("/")
    return `${year}-${month}-${day}`
  }

  const getDashboardData = useCallback(async () => {
    try {
      setLoading(true)
      const startDate = formatToISO(dateRange.from)
      const endDate = formatToISO(dateRange.to)
      const params: any = {}

      if (startDate) params.dataInicio = startDate
      if (endDate) params.dataFim = endDate

      const response = await getDashboard(params)
      setDashboardData(response.data.data)
    } catch (error) {
      console.error(error)
    } finally {
      setLoading(false)
    }
  }, [dateRange.from, dateRange.to])

  useEffect(() => {
    getDashboardData()
  }, [])

  // --- Configurações de Gráficos ---

  const baseChartOptions: ApexCharts.ApexOptions = {
    theme: { mode: "dark" },
    chart: { background: "transparent", toolbar: { show: false } },
    grid: { borderColor: "#334155" },
    legend: { labels: { colors: "#94a3b8" }, position: "bottom" },
  }

  const sessionsOptions: ApexCharts.ApexOptions = {
    ...baseChartOptions,
    colors: ["#3b82f6", "#10b981"],
    plotOptions: { bar: { borderRadius: 4, columnWidth: "45%" } },
    xaxis: {
      categories: ["Online", "In Company"],
      labels: { style: { colors: "#94a3b8" } },
    },
  }

  const distributionOptions: ApexCharts.ApexOptions = {
    ...baseChartOptions,
    labels: ["Especialista", "Principal"],
    colors: ["#6366f1", "#ec4899"],
    stroke: { show: false },
    plotOptions: { pie: { donut: { size: "70%" } } },
  }

  const comparisonOptions: ApexCharts.ApexOptions = {
    ...baseChartOptions,
    chart: { ...baseChartOptions.chart, stacked: true },
    colors: ["#3b82f6", "#6366f1"],
    plotOptions: { bar: { horizontal: true, barHeight: "60%", borderRadius: 4 } },
    xaxis: {
      categories: comparisonChartData.categories,
      labels: { style: { colors: "#94a3b8" } },
    },
    yaxis: { labels: { style: { colors: "#94a3b8" } } },
    tooltip: { theme: "dark" },
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col justify-between gap-4 rounded-xl border border-dark-300 bg-dark-500 p-4 md:flex-row md:items-center">
        <div>
          <Text
            variant="subtitle"
            className="text-white"
          >
            Filtros de Período
          </Text>
          <Text
            variant="body"
            className="text-dark-300"
          >
            Refine os dados do dashboard
          </Text>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <Input
              type="text"
              placeholder="DD/MM/AAAA"
              className="max-w-[140px] bg-transparent outline-none"
              value={dateRange.from}
              onChange={(e) => onDateChange("from", dateMask(e.target.value))}
            />
            <span className="text-dark-300">até</span>
            <Input
              type="text"
              placeholder="DD/MM/AAAA"
              className="max-w-[140px] bg-transparent outline-none"
              value={dateRange.to}
              onChange={(e) => onDateChange("to", dateMask(e.target.value))}
            />
          </div>

          <Button
            onClick={getDashboardData}
            disabled={loading}
            size="sm"
            icon="filter"
          >
            {loading ? "Buscando..." : "Filtrar"}
          </Button>
        </div>
      </div>

      {loading && !dashboardData ? (
        <div className="flex h-64 items-center justify-center">
          <Text className="text-white">Carregando indicadores...</Text>
        </div>
      ) : (
        dashboardData && (
          <>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <div className="rounded-xl border border-l-4 border-dark-300 border-l-indigo-500 bg-dark-500 p-5 shadow-lg">
                <Text
                  variant="label"
                  className="mb-3 block uppercase text-dark-100"
                >
                  {isAdmin ? "Mentores como Especialistas" : "Empresas que sou Mentor Especialista"}
                </Text>
                <div className="flex items-center gap-8">
                  {isAdmin ? (
                    <>
                      <div className="flex flex-col">
                        <span className="text-3xl font-bold text-white">
                          {dashboardData.qtdMentoresEspecialistasUnicos || 0}
                        </span>
                        <span className="mt-1 text-xs uppercase text-dark-100">Mentores</span>
                      </div>
                      <div className="flex size-8 items-center justify-center rounded-full bg-white text-xs text-dark">
                        em
                      </div>
                      <div className="flex flex-col">
                        <span className="text-3xl font-bold text-white">
                          {dashboardData.qtdMentorEspecialista}
                        </span>
                        <span className="mt-1 text-xs uppercase text-dark-100">Empresas</span>
                      </div>
                    </>
                  ) : (
                    <span className="text-3xl font-bold text-white">
                      {dashboardData.qtdMentorEspecialista}
                    </span>
                  )}
                </div>
              </div>

              <div className="rounded-xl border border-l-4 border-dark-300 border-l-primary bg-dark-500 p-5 shadow-lg">
                <Text
                  variant="label"
                  className="mb-3 block uppercase text-dark-100"
                >
                  {isAdmin ? "Mentores como Principais" : "Empresas que sou Mentor Principal"}
                </Text>
                <div className="flex items-center gap-8">
                  {isAdmin ? (
                    <>
                      <div className="flex flex-col">
                        <span className="text-3xl font-bold text-white">
                          {dashboardData.qtdMentoresPrincipaisUnicos || 0}
                        </span>
                        <span className="mt-1 text-xs uppercase text-dark-100">Mentores</span>
                      </div>
                      <div className="flex size-8 items-center justify-center rounded-full bg-white text-xs text-dark">
                        em
                      </div>
                      <div className="flex flex-col">
                        <span className="text-3xl font-bold text-white">
                          {dashboardData.qtdMentorPrincipal}
                        </span>
                        <span className="mt-1 text-xs uppercase text-dark-100">Empresas</span>
                      </div>
                    </>
                  ) : (
                    <span className="text-3xl font-bold text-white">
                      {dashboardData.qtdMentorPrincipal}
                    </span>
                  )}
                </div>
              </div>

              <div className="rounded-xl border border-l-4 border-dark-300 border-l-pink-500 bg-dark-500 p-5 shadow-lg">
                <Text
                  variant="label"
                  className="mb-3 block uppercase text-dark-100"
                >
                  Empresas Giants
                </Text>
                <Text className="text-3xl font-bold text-white">
                  {dashboardData.qtdEmpresasGiants}
                </Text>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              <div className="rounded-xl border border-dark-300 bg-dark-500 p-4">
                <Text
                  variant="label"
                  className="mb-4 block text-dark-100"
                >
                  Sessões por Tipo
                </Text>
                <Chart
                  options={sessionsOptions}
                  series={[
                    {
                      name: "Sessões",
                      data: [num(dashboardData.sessoesOnline), num(dashboardData.sessoesInCompany)],
                    },
                  ]}
                  type="bar"
                  height={250}
                />
              </div>

              <div className="rounded-xl border border-dark-300 bg-dark-500 p-4">
                <Text
                  variant="label"
                  className="mb-4 block text-dark-100"
                >
                  Distribuição de Mentorias
                </Text>
                <Chart
                  options={distributionOptions}
                  series={[
                    num(dashboardData.realizadasComoEspecialista),
                    num(dashboardData.realizadasComoPrincipal),
                  ]}
                  type="donut"
                  height={250}
                />
              </div>
            </div>

            {isAdmin && (
              <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                <div className="rounded-xl border border-dark-300 bg-dark-500 p-6 shadow-lg">
                  <div className="mb-6">
                    <Text
                      variant="subtitle"
                      className="text-white"
                    >
                      Comparativo de Atuação (Top 10)
                    </Text>
                    <Text
                      variant="body"
                      className="text-dark-300"
                    >
                      Ordenado pelo volume total de empresas
                    </Text>
                  </div>
                  <Chart
                    options={comparisonOptions}
                    series={comparisonChartData.series}
                    type="bar"
                    height={350}
                  />
                </div>

                <div className="flex flex-col gap-4 rounded-xl border border-dark-300 bg-dark-500 p-6 shadow-lg">
                  <div className="flex flex-col justify-between gap-4 border-b border-dark-400 pb-6 md:flex-row md:items-center">
                    <div>
                      <Text
                        variant="subtitle"
                        className="text-white"
                      >
                        Performance por Mentor
                      </Text>
                      <Text
                        variant="body"
                        className="text-dark-300"
                      >
                        Atuação individual detalhada
                      </Text>
                    </div>
                    <div className="relative w-full md:max-w-[300px]">
                      <Input
                        placeholder="Buscar mentor pelo nome..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="custom-scrollbar flex max-h-[500px] flex-col gap-3 overflow-y-auto pr-2">
                    {filteredMentores.length > 0 ? (
                      filteredMentores.map((mentor) => (
                        <div
                          key={mentor.mentorId}
                          className="flex flex-col items-start justify-between gap-4 rounded-xl border border-dark-400 bg-dark-400/20 p-4 transition-all hover:bg-dark-400/40 md:flex-row md:items-center"
                        >
                          <div className="flex items-center gap-4">
                            <div className="flex size-10 shrink-0 items-center justify-center rounded-full border border-indigo-500/20 bg-indigo-500/10 font-bold text-indigo-400">
                              {mentor.mentorNome.charAt(0)}
                            </div>
                            <div className="flex flex-col">
                              <span className="font-bold leading-tight text-white">
                                {mentor.mentorNome}
                              </span>
                              <span className="text-[10px] uppercase tracking-widest text-dark-300">
                                ID: #{mentor.mentorId}
                              </span>
                            </div>
                          </div>

                          <div className="grid w-full grid-cols-3 gap-2 border-t border-dark-400 pt-3 md:flex md:w-auto md:gap-10 md:border-t-0 md:pt-0">
                            <div className="flex flex-col md:items-center">
                              <span className="text-[10px] uppercase text-dark-100">Principal</span>
                              <span className="text-base font-semibold text-white">
                                {mentor.qtdEmpresasPrincipal}
                              </span>
                            </div>
                            <div className="flex flex-col md:items-center">
                              <span className="text-[10px] uppercase text-dark-100">
                                Especialista
                              </span>
                              <span className="text-base font-semibold text-white">
                                {mentor.qtdEmpresasEspecialista}
                              </span>
                            </div>
                            <div className="flex flex-col md:items-end">
                              <span className="text-[10px] font-bold uppercase text-indigo-400/80">
                                Sessões
                              </span>
                              <span className="text-base font-bold text-indigo-400">
                                {mentor.sessoesRealizadas}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="py-10 text-center italic text-dark-300">
                        Nenhum mentor encontrado.
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </>
        )
      )}
    </div>
  )
}
